import React, { useState, useMemo } from 'react';
import { useEditMode } from '@/contexts/EditModeContext';
import { 
  Wrench,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Package,
  Hammer,
  CircleDollarSign,
  Car,
  Settings,
  Cog,
  PaintBucket,
  CircuitBoard,
  Gauge,
  Zap
} from 'lucide-react';
import { formatCurrency } from '@/data/inspectionData';

// Category icons mapping
const categoryIcons = {
  'engine': Settings,
  'transmission': Cog,
  'suspension': Car,
  'brakes': CircuitBoard,
  'electrical': Zap,
  'body': PaintBucket,
  'interior': Package,
  'exterior': Car,
  'ac': Gauge,
  'default': Wrench
};

// Component ID to display name mapping
const COMPONENT_DISPLAY_NAMES = {
  'body_panels': 'Body Panels',
  'mechanical': 'Mechanical Parts',
  'electrical': 'Electrical',
  'interior': 'Interior',
  'glass': 'Glass & Mirrors',
  'tyres_wheels': 'Tyres & Wheels',
  'ac_cooling': 'AC & Cooling',
  'exhaust': 'Exhaust System',
  'general_repairs': 'General Repairs',
};

// Helper to get component display name
const getComponentDisplayName = (componentId) => {
  return COMPONENT_DISPLAY_NAMES[componentId] || componentId || 'General Repairs';
};

const getCategoryIcon = (category) => {
  const cat = (category || '').toLowerCase();
  for (const [key, Icon] of Object.entries(categoryIcons)) {
    if (cat.includes(key)) return Icon;
  }
  return categoryIcons.default;
};

// Priority badge component
const PriorityBadge = ({ priority }) => {
  const getPriorityStyle = () => {
    const p = typeof priority === 'number' ? priority : (priority || '').toLowerCase();
    if (p === 1 || p === 'critical' || p === 'high') return 'bg-red-100 text-red-700 border-red-200';
    if (p === 2 || p === 'medium' || p === 'important') return 'bg-orange-100 text-orange-700 border-orange-200';
    return 'bg-green-100 text-green-700 border-green-200';
  };

  const getLabel = () => {
    const p = typeof priority === 'number' ? priority : (priority || '').toLowerCase();
    if (p === 1 || p === 'critical' || p === 'high') return 'High Priority';
    if (p === 2 || p === 'medium' || p === 'important') return 'Medium';
    return 'Low';
  };

  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${getPriorityStyle()}`}>
      {getLabel()}
    </span>
  );
};

// Action badge component  
const ActionBadge = ({ action }) => {
  const isReplace = (action || '').toLowerCase() === 'replace';
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
      isReplace ? 'bg-red-500 text-white' : 'bg-amber-100 text-amber-700'
    }`}>
      {isReplace ? 'REPLACE' : 'REPAIR'}
    </span>
  );
};

// Single repair item component
const RepairItem = ({ repair, isLast }) => {
  const Icon = getCategoryIcon(repair.category || repair.category_name);
  
  return (
    <div className={`flex items-start gap-3 py-3 ${!isLast ? 'border-b border-border/50' : ''}`}>
      <div className="p-2 rounded-lg bg-secondary/50 flex-shrink-0">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <ActionBadge action={repair.action || repair.type} />
          <PriorityBadge priority={repair.priority} />
        </div>
        <p className="font-medium text-sm text-foreground">{repair.part_name || repair.description}</p>
        {repair.category_name && (
          <p className="text-xs text-muted-foreground mt-0.5">{repair.category_name}</p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="font-semibold text-foreground">{formatCurrency(repair.cost || repair.estimated_cost || 0)}</p>
        {(repair.price > 0 || repair.labor > 0) && (
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {repair.price > 0 && <span>Part: {formatCurrency(repair.price)}</span>}
            {repair.price > 0 && repair.labor > 0 && <span> • </span>}
            {repair.labor > 0 && <span>Labor: {formatCurrency(repair.labor)}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

// Category group component (collapsible)
const CategoryGroup = ({ category, repairs, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const Icon = getCategoryIcon(category);
  const totalCost = repairs.reduce((sum, r) => sum + (r.cost || r.estimated_cost || 0), 0);
  const replaceCount = repairs.filter(r => (r.action || r.type || '').toLowerCase() === 'replace').length;
  const repairCount = repairs.length - replaceCount;

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 md:p-4 hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-accent/10">
            <Icon className="h-4 w-4 md:h-5 md:w-5 text-accent" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-sm md:text-base">{category}</p>
            <p className="text-xs text-muted-foreground">
              {repairs.length} item{repairs.length !== 1 ? 's' : ''}
              {replaceCount > 0 && <span className="text-red-600 ml-1">({replaceCount} replace)</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-bold text-accent">{formatCurrency(totalCost)}</span>
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>
      
      {isOpen && (
        <div className="px-3 md:px-4 pb-3 md:pb-4 border-t border-border/50">
          {repairs.map((repair, idx) => (
            <RepairItem 
              key={repair.id || repair.part_id || idx} 
              repair={repair} 
              isLast={idx === repairs.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Summary card component
const SummaryCard = ({ icon: Icon, label, value, subtext, variant = 'default' }) => {
  const variants = {
    default: 'bg-secondary/30',
    highlight: 'bg-accent/10 border border-accent/20',
    warning: 'bg-orange-50 border border-orange-200',
    danger: 'bg-red-50 border border-red-200'
  };

  return (
    <div className={`rounded-xl p-3 md:p-4 ${variants[variant]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-xl md:text-2xl font-bold text-foreground">{value}</p>
      {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
    </div>
  );
};

export function RepairsSection({ data }) {
  const { isEditMode } = useEditMode();
  
  // Get repairs from various data formats
  const allRepairs = useMemo(() => {
    // Try to get repairs from different possible structures
    const repairs = data?.repairs || data?.calculatedRepairs || [];
    const minorRepairs = (data?.minorRepairs || []).map(r => ({ ...r, type: 'minor' }));
    const majorRepairs = (data?.majorRepairs || []).map(r => ({ ...r, type: 'major' }));
    
    // Combine all repairs then deduplicate by part_name
    const combined = [...repairs, ...minorRepairs, ...majorRepairs];
    const seen = new Set();
    return combined.filter(r => {
      const key = (r.part_name || r.name || '').toLowerCase().trim();
      if (key && seen.has(key)) return false;
      if (key) seen.add(key);
      return true;
    });
  }, [data]);

  // Group repairs by component (part.category) for accordion display
  const repairsByCategory = useMemo(() => {
    const groups = {};
    allRepairs.forEach(repair => {
      // Use component (repair.category) for grouping, get display name
      const componentId = repair.category || 
        ((repair.type || '').toLowerCase() === 'major' ? 'major_repairs' : 'general_repairs');
      const componentDisplayName = getComponentDisplayName(componentId);
      if (!groups[componentDisplayName]) {
        groups[componentDisplayName] = [];
      }
      groups[componentDisplayName].push(repair);
    });
    
    // Sort components by total cost (highest first)
    return Object.entries(groups)
      .sort((a, b) => {
        const totalA = a[1].reduce((sum, r) => sum + (r.cost || r.estimated_cost || 0), 0);
        const totalB = b[1].reduce((sum, r) => sum + (r.cost || r.estimated_cost || 0), 0);
        return totalB - totalA;
      });
  }, [allRepairs]);

  // Calculate totals
  const totals = useMemo(() => {
    const partsTotal = allRepairs.reduce((sum, r) => sum + (r.price || 0), 0);
    const laborTotal = allRepairs.reduce((sum, r) => sum + (r.labor || 0), 0);
    const grandTotal = allRepairs.reduce((sum, r) => sum + (r.cost || r.estimated_cost || 0), 0);
    const replaceCount = allRepairs.filter(r => (r.action || r.type || '').toLowerCase() === 'replace').length;
    const repairCount = allRepairs.length - replaceCount;
    const highPriorityCount = allRepairs.filter(r => {
      const p = typeof r.priority === 'number' ? r.priority : (r.priority || '').toLowerCase();
      return p === 1 || p === 'critical' || p === 'high';
    }).length;

    return { partsTotal, laborTotal, grandTotal, replaceCount, repairCount, highPriorityCount, totalItems: allRepairs.length };
  }, [allRepairs]);

  // Don't render if no repairs
  if (allRepairs.length === 0) {
    return (
      <section className="px-4 md:px-0 mt-4 md:mt-6">
        <div className="mobile-card md:rounded-2xl">
          <div className="mobile-card-header flex items-center gap-2">
            <div className="p-1.5 md:p-2 rounded-lg bg-accent/10">
              <Wrench className="h-4 w-4 md:h-5 md:w-5 text-accent" />
            </div>
            <h2 className="font-semibold font-display text-base md:text-lg">Estimated Repairs</h2>
          </div>
          <div className="mobile-card-body">
            <div className="flex items-center justify-center py-8 text-center">
              <div>
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="font-medium text-foreground">No Repairs Required</p>
                <p className="text-sm text-muted-foreground mt-1">This vehicle is in good condition</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 md:px-0 mt-4 md:mt-6">
      <div className="mobile-card md:rounded-2xl">
        {/* Header */}
        <div className="mobile-card-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 md:p-2 rounded-lg bg-accent/10">
              <Wrench className="h-4 w-4 md:h-5 md:w-5 text-accent" />
            </div>
            <div>
              <h2 className="font-semibold font-display text-base md:text-lg">Estimated Repairs</h2>
              <p className="text-xs text-muted-foreground">{totals.totalItems} items identified</p>
            </div>
          </div>
          {totals.highPriorityCount > 0 && (
            <div className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded-full">
              <AlertTriangle className="h-3 w-3" />
              <span className="text-xs font-medium">{totals.highPriorityCount} High Priority</span>
            </div>
          )}
        </div>
        
        <div className="mobile-card-body space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            <SummaryCard 
              icon={CircleDollarSign}
              label="Total Cost"
              value={formatCurrency(totals.grandTotal)}
              variant="highlight"
            />
            <SummaryCard 
              icon={Package}
              label="Parts Cost"
              value={formatCurrency(totals.partsTotal)}
              subtext={`${totals.totalItems} components`}
            />
            <SummaryCard 
              icon={Hammer}
              label="Labor Cost"
              value={formatCurrency(totals.laborTotal)}
            />
            <SummaryCard 
              icon={AlertTriangle}
              label="Replacements"
              value={totals.replaceCount}
              subtext={`${totals.repairCount} repairs`}
              variant={totals.replaceCount > 0 ? 'warning' : 'default'}
            />
          </div>

          {/* Cost Breakdown Bar */}
          {totals.grandTotal > 0 && (
            <div className="bg-secondary/30 rounded-xl p-3 md:p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Cost Breakdown</p>
              <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                {totals.partsTotal > 0 && (
                  <div 
                    className="h-full bg-accent" 
                    style={{ width: `${(totals.partsTotal / totals.grandTotal) * 100}%` }}
                    title={`Parts: ${formatCurrency(totals.partsTotal)}`}
                  />
                )}
                {totals.laborTotal > 0 && (
                  <div 
                    className="h-full bg-orange-500" 
                    style={{ width: `${(totals.laborTotal / totals.grandTotal) * 100}%` }}
                    title={`Labor: ${formatCurrency(totals.laborTotal)}`}
                  />
                )}
              </div>
              <div className="flex justify-between mt-2 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  <span className="text-muted-foreground">Parts ({Math.round((totals.partsTotal / totals.grandTotal) * 100)}%)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-orange-500" />
                  <span className="text-muted-foreground">Labor ({Math.round((totals.laborTotal / totals.grandTotal) * 100)}%)</span>
                </div>
              </div>
            </div>
          )}

          {/* Category-wise Repairs */}
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Component-wise Breakdown</p>
            {repairsByCategory.map(([category, repairs], idx) => (
              <CategoryGroup 
                key={category} 
                category={category} 
                repairs={repairs}
                defaultOpen={false}
              />
            ))}
          </div>

          {/* Grand Total Footer */}
          <div className="bg-gradient-to-r from-accent/10 to-accent/5 rounded-xl p-4 border border-accent/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Estimated Total Repairs</p>
                <p className="text-2xl md:text-3xl font-bold text-accent mt-1">{formatCurrency(totals.grandTotal)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">
                  {totals.replaceCount} replacement{totals.replaceCount !== 1 ? 's' : ''} • {totals.repairCount} repair{totals.repairCount !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Across {repairsByCategory.length} component{repairsByCategory.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default RepairsSection;
